import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  query, where, updateDoc,
} from "firebase/firestore";
import {
  getDownloadURL,
  listAll,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { db, storage } from "../firebase/firebase";

import { AuthContext } from "../context/AuthContext";

import "../styles/AdminTools.css";

const MAX_FILES = 10;

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatSeconds(sec) {
  if (!isFinite(sec) || sec < 0) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s}s`;
}

export default function ResourcePanel({ onBackClick }) {
  const { userData, authLoading } = useContext(AuthContext);

  // Role checks (safe even if userData missing)
  const role = userData?.role || "user";
  const canManageUsers = role === "admin";
  const canManageResources = role === "admin" || role === "uploader";

  const [groups, setGroups] = useState([]);
  const [folders, setFolders] = useState([]); // includes subfolders
  const [showPermModal, setShowPermModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editAllowedGroups, setEditAllowedGroups] = useState([]);
  const [savingPerms, setSavingPerms] = useState(false);

  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [parentId, setParentId] = useState(""); // "" = top-level
  const [allowedGroups, setAllowedGroups] = useState([]);
  const selectableGroupNames =
  role === "admin"
    ? groups.map((g) => g.name)
    : (userData?.groups || []);

  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [folderDocs, setFolderDocs] = useState([]);

  // Drag/drop upload state
  const [files, setFiles] = useState([]); // File[]
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]); // [{name, progress, status, bytesTransferred, totalBytes, speedBps, etaSec}]
  const uploadStartRef = useRef({}); // { [fileName]: startTimeMs }
  

  useEffect(() => {
    // Only load what user is allowed to see.
    // Resource managers need folders & storage previews. Users shouldn't be here anyway.
    if (!authLoading && canManageResources) {
      loadAll().catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, canManageResources]);

  const loadAll = async () => {
    const tasks = [
      getFoldersForUser(userData),
    ];

    // Only admins can read groups if your rules restrict it.
    // If your rules allow uploader read, you can remove this guard.
    if (canManageUsers) tasks.push(getDocs(collection(db, "groups")));

    const results = await Promise.all(tasks);

    const foldersSnap = results[0];
    setFolders(foldersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    if (canManageUsers) {
      const groupsSnap = results[1];
      setGroups(groupsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } else {
      setGroups([]); // uploader doesn’t need to assign groups (optional)
    }
  };

  const topFolders = useMemo(() => folders.filter((f) => !f.parentId), [folders]);
  const childrenOf = (id) => folders.filter((f) => f.parentId === id);

  const toggleAllowedGroup = (name) => {
    setAllowedGroups((prev) =>
      prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name]
    );
  };

  const createFolder = async () => {
    if (!canManageResources) return alert("You do not have permission to create folders.");
    if (!folderName.trim()) return alert("Enter folder name");

    const userGroups = userData?.groups || [];

    let finalAllowedGroups = allowedGroups;

    // uploader: force allowedGroups to be subset of their groups
    if (role === "uploader") {
      finalAllowedGroups = allowedGroups.filter((g) => userGroups.includes(g));
      if (finalAllowedGroups.length === 0) {
        return alert("Pick at least one group you belong to.");
      }
    }

    await addDoc(collection(db, "folders"), {
      name: folderName.trim(),
      parentId: parentId || null,
      allowedGroups: canManageUsers ? allowedGroups : finalAllowedGroups,
      createdAt: serverTimestamp(),
    });

    setFolderName("");
    setParentId("");
    setAllowedGroups([]);
    setShowCreateFolder(false);
    await loadAll();
  };
  const openPermModal = (folder) => {
    setEditingFolder(folder);
    setEditAllowedGroups(folder.allowedGroups || []);
    setShowPermModal(true);
  };

  const closePermModal = () => {
    setShowPermModal(false);
    setEditingFolder(null);
    setEditAllowedGroups([]);
  };

  const toggleEditGroup = (name) => {
    setEditAllowedGroups((prev) =>
      prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name]
    );
  };

  const savePermissions = async () => {
    if (!editingFolder) return;

    const userGroups = userData?.groups || [];
    let finalGroups = editAllowedGroups;

    // uploader: must be subset of their groups (keeps your existing security assumption)
    if (role === "uploader") {
      finalGroups = finalGroups.filter((g) => userGroups.includes(g));
      if (finalGroups.length === 0) {
        return alert("Pick at least one group you belong to.");
      }
    }

    try {
      setSavingPerms(true);
      await updateDoc(doc(db, "folders", editingFolder.id), {
        allowedGroups: finalGroups,
        updatedAt: serverTimestamp(),
      });
      await loadAll();
      closePermModal();
    } catch (e) {
      console.error(e);
      alert("Failed to update permissions.");
    } finally {
      setSavingPerms(false);
    }
  };

  const deleteFolder = async (id) => {
    if (!canManageResources) return alert("You do not have permission to delete folders.");
    if (!window.confirm("Delete this folder entry? (Storage files will remain.)")) return;
    await deleteDoc(doc(db, "folders", id));
    await loadAll();
  };

  const buildFolderPath = (folderId) => `folders/${folderId}`;

  const loadFolderDocs = async (folderId) => {
    setSelectedFolderId(folderId);
    setFolderDocs([]);
    if (!folderId) return;

    try {
      const folderRef = ref(storage, buildFolderPath(folderId));
      const fileList = await listAll(folderRef);
      const docs = await Promise.all(
        fileList.items.map(async (item) => ({
          name: item.name,
          url: await getDownloadURL(item),
        }))
      );
      setFolderDocs(docs);
    } catch (e) {
      console.error(e);
      setFolderDocs([]);
    }
  };

  // ---------- Drag & Drop helpers ----------
  const addFiles = (incomingFiles) => {
    const incoming = Array.from(incomingFiles || []);
    if (incoming.length === 0) return;

    const merged = [...files, ...incoming];

    // de-dup by (name + size + lastModified)
    const uniq = [];
    const seen = new Set();
    for (const f of merged) {
      const key = `${f.name}|${f.size}|${f.lastModified}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniq.push(f);
      }
    }

    if (uniq.length > MAX_FILES) {
      alert(`You can upload up to ${MAX_FILES} files at a time.`);
      setFiles(uniq.slice(0, MAX_FILES));
      return;
    }

    setFiles(uniq);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearFiles = () => {
    setFiles([]);
  };

  const uploadMany = async () => {
    if (!canManageResources) return alert("You do not have permission to upload files.");
    if (!selectedFolderId) return alert("Select a folder to upload into");
    if (!files.length) return alert("Select one or more files");
    if (files.length > MAX_FILES) return alert(`Max ${MAX_FILES} files allowed`);

    setUploading(true);

    // initialize upload entries
    const initial = files.map((f) => ({
      name: f.name,
      progress: 0,
      status: "queued", // queued | uploading | done | error
      bytesTransferred: 0,
      totalBytes: f.size,
      speedBps: 0,
      etaSec: Infinity,
    }));
    setUploads(initial);

    const folderPath = buildFolderPath(selectedFolderId);

    // Run uploads in parallel (10 max already)
    await Promise.all(
      files.map((file) => {
        return new Promise((resolve) => {
          const storageRef = ref(storage, `${folderPath}/${file.name}`);
          const task = uploadBytesResumable(storageRef, file);

          uploadStartRef.current[file.name] = Date.now();

          task.on(
            "state_changed",
            (snapshot) => {
              const now = Date.now();
              const start = uploadStartRef.current[file.name] || now;
              const dtSec = Math.max(0.2, (now - start) / 1000);
              const speed = snapshot.bytesTransferred / dtSec; // B/s
              const remaining = snapshot.totalBytes - snapshot.bytesTransferred;
              const eta = speed > 0 ? remaining / speed : Infinity;
              const pct =
                snapshot.totalBytes > 0
                  ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                  : 0;

              setUploads((prev) =>
                prev.map((u) =>
                  u.name === file.name
                    ? {
                        ...u,
                        status: "uploading",
                        progress: Math.min(100, Math.max(0, pct)),
                        bytesTransferred: snapshot.bytesTransferred,
                        totalBytes: snapshot.totalBytes,
                        speedBps: speed,
                        etaSec: eta,
                      }
                    : u
                )
              );
            },
            (err) => {
              console.error("Upload error:", err);
              setUploads((prev) =>
                prev.map((u) =>
                  u.name === file.name ? { ...u, status: "error" } : u
                )
              );
              resolve(); // continue even if one fails
            },
            () => {
              setUploads((prev) =>
                prev.map((u) =>
                  u.name === file.name
                    ? { ...u, status: "done", progress: 100, etaSec: 0 }
                    : u
                )
              );
              resolve();
            }
          );
        });
      })
    );

    setUploading(false);

    // refresh file list preview
    await loadFolderDocs(selectedFolderId);

    // clear local selection
    clearFiles();
    const input = document.getElementById("multiFileInput");
    if (input) input.value = "";

    alert("Upload complete (check any failed items marked as error).");
  };

  const renderFolderTree = (folder, depth = 0) => {
    const kids = childrenOf(folder.id);
    return (
      <div key={folder.id} style={{ marginLeft: depth ? 14 : 0 }}>
        <div className="admin-folder-row">
          <button
            className={`admin-folder-btn ${
              selectedFolderId === folder.id ? "is-active" : ""
            }`}
            type="button"
            onClick={() => loadFolderDocs(folder.id)}
          >
            {depth ? "↳" : "📁"} {folder.name}
          </button>

          <div className="admin-folder-meta">
            <span className="admin-muted">
              {folder.allowedGroups?.length
                ? `Groups: ${folder.allowedGroups.join(", ")}`
                : "Groups: none"}
            </span>

             {canManageResources && (
                <>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => openPermModal(folder)}
                  >
                    Edit
                  </button>

                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => deleteFolder(folder.id)}
                  >
                    Delete
                  </button>
                </>
              )}
          </div>
        </div>

        {kids.length ? (
          <div style={{ marginTop: 6 }}>
            {kids.map((c) => renderFolderTree(c, depth + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  // ---------- Guard UI ----------
  if (authLoading) {
    return (
      <div className="content-area">
        <div className="admin-muted">Loading…</div>
      </div>
    );
  }

  if (!canManageResources) {
    return (
      <div className="content-area">
        <div className="page-header">
          <h1 className="page-title">Resource Panel</h1>
          <p className="page-subtitle">You don’t have permission to access this page.</p>
          <button className="btn btn-secondary" type="button" onClick={onBackClick}>
            ← Back
          </button>
        </div>

        <div className="admin-info">
          <strong>Access required</strong>
          <p className="admin-muted" style={{ marginTop: 8 }}>
            Ask your administrator to assign you the <b>uploader</b> or <b>admin</b> role.
          </p>
        </div>
      </div>
    );
  }

  // ---------- Main UI ----------
  return (
    <div className="content-area admin-shell">
      <div className="page-header">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1 className="page-title">
              Resource Uploader Panel
            </h1>
            <p className="page-subtitle">
                Create folders and upload resources
            </p>

            {!userData && (
              <div className="admin-info" style={{ marginTop: 10 }}>
                <strong>Note</strong>
                <p className="admin-muted" style={{ marginTop: 8 }}>
                  Your user profile (role/groups) is not loaded yet. If this persists,
                  check Firestore permissions for <code>users/{`{uid}`}</code>.
                </p>
              </div>
            )}
          </div>

          <button className="btn btn-secondary" type="button" onClick={onBackClick}>
            ← Back
          </button>
        </div>
      </div>

      {/* Folder + Subfolder Management */}
      {canManageResources && (
        <div className="admin-card form-container">
        <div className="admin-card-header">
          <div>
            <h2 className="admin-card-title">📁 Folder & Subfolder Management</h2>
            <p className="admin-card-subtitle">
              Create folders/subfolders. Control access via groups.
            </p>
          </div>

          <button
            className="btn btn-primary"
            type="button"
            onClick={() => setShowCreateFolder((s) => !s)}
          >
            {showCreateFolder ? "Close" : "Create Folder"}
          </button>
        </div>

        {showCreateFolder && (
          <div className="admin-surface">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Folder name</label>
                <input
                  className="form-input"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  placeholder="e.g., Assessments, Policies, Unit Materials"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Parent folder (optional)</label>
                <select
                  className="form-select"
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                >
                  <option value="">Top-level (no parent)</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
                <div className="admin-hint">Choose a parent to create a subfolder.</div>
              </div>
            </div>

            {/* Only ADMIN can assign groups (you can remove this if uploaders should also set groups) */}
            {selectableGroupNames.length === 0 ? (
              <div className="admin-muted">No groups available.</div>
            ) : (
              <div className="admin-chip-grid">
                {selectableGroupNames.map((name) => (
                  <label key={name} className="admin-chip">
                    <input
                      type="checkbox"
                      checked={allowedGroups.includes(name)}
                      onChange={() => toggleAllowedGroup(name)}
                    />
                    <span>{name}</span>
                  </label>
                ))}
              </div>
            )}

            <div className="form-actions">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setShowCreateFolder(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" type="button" onClick={createFolder}>
                Save Folder
              </button>
            </div>
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          <h3 className="admin-subtitle">Folders</h3>
          {topFolders.length === 0 ? (
            <div className="admin-muted">No folders created yet.</div>
          ) : (
            <div className="admin-folder-tree">
              {topFolders.map((f) => renderFolderTree(f))}
            </div>
          )}
        </div>
      </div>
      )}
      {canManageResources && (
      <div className="admin-card form-container">
        <div className="admin-card-header">
          <div>
            <h2 className="admin-card-title">📤 Upload (drag & drop, up to {MAX_FILES})</h2>
            <p className="admin-card-subtitle">
              Select a folder/subfolder and upload multiple files with progress tracking.
            </p>
          </div>
        </div>

        <div className="admin-surface">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Upload into</label>
              <select
                className="form-select"
                value={selectedFolderId}
                onChange={(e) => loadFolderDocs(e.target.value)}
                disabled={uploading}
              >
                <option value="">Select folder...</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.parentId ? `↳ ${f.name}` : f.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Files</label>

              <div
                className={`admin-dropzone ${uploading ? "is-disabled" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (uploading) return;
                  addFiles(e.dataTransfer.files);
                }}
              >
                <div className="admin-dropzone-title">
                  Drag & drop files here
                </div>
                <div className="admin-dropzone-sub">
                  or choose files (max {MAX_FILES})
                </div>

                <input
                  id="multiFileInput"
                  className="admin-dropzone-input"
                  type="file"
                  multiple
                  disabled={uploading}
                  onChange={(e) => addFiles(e.target.files)}
                />
              </div>

              <div className="admin-hint">
                Selected: {files.length ? `${files.length} file(s)` : "none"}
              </div>

              {files.length > 0 && (
                <div className="admin-file-list">
                  {files.map((f, idx) => (
                    <div className="admin-file-row" key={`${f.name}-${f.size}-${f.lastModified}`}>
                      <div>
                        <div className="admin-file-name">📄 {f.name}</div>
                        <div className="admin-muted">{formatBytes(f.size)}</div>
                      </div>

                      {!uploading && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => removeFile(idx)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}

                  {!uploading && (
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                      <button type="button" className="btn btn-secondary" onClick={clearFiles}>
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={uploadMany}
              disabled={!selectedFolderId || !files.length || uploading}
            >
              {uploading ? "Uploading..." : "Upload Files"}
            </button>
          </div>

          {/* Upload progress list */}
          {uploads.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <h3 className="admin-subtitle">Upload Progress</h3>
              <div className="admin-progress-list">
                {uploads.map((u) => (
                  <div className="admin-progress-row" key={u.name}>
                    <div className="admin-progress-top">
                      <div className="admin-file-name">{u.name}</div>
                      <div className="admin-muted" style={{ textAlign: "right" }}>
                        {u.status === "error"
                          ? "Error"
                          : u.status === "done"
                          ? "Done"
                          : `${Math.round(u.progress)}%`}
                      </div>
                    </div>

                    <div className="admin-progress-bar">
                      <div
                        className={`admin-progress-fill ${
                          u.status === "error" ? "is-error" : ""
                        }`}
                        style={{ width: `${u.progress}%` }}
                      />
                    </div>

                    <div className="admin-progress-meta">
                      <span className="admin-muted">
                        {formatBytes(u.bytesTransferred)} / {formatBytes(u.totalBytes)}
                      </span>
                      <span className="admin-muted">
                        {u.status === "uploading"
                          ? `${formatBytes(u.speedBps)}/s • ETA ${formatSeconds(u.etaSec)}`
                          : " "}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Preview docs */}
        <div style={{ marginTop: 18 }}>
          <h3 className="admin-subtitle">Files in selected folder</h3>
          {!selectedFolderId ? (
            <div className="admin-muted">Select a folder to view files.</div>
          ) : folderDocs.length === 0 ? (
            <div className="admin-muted">No files found in this folder.</div>
          ) : (
            <div className="admin-doc-list">
              {folderDocs.map((d) => (
                <a
                  key={d.name}
                  className="admin-doc-row"
                  href={d.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>📄 {d.name}</span>
                  <span className="admin-doc-open">Open</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
      )}
      {showPermModal && editingFolder && (
        <div
          className="admin-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePermModal();
          }}
        >
          <div className="admin-modal">
            <div className="admin-modal-header">
              <div>
                <h3 className="admin-modal-title">Edit Folder Permissions</h3>
                <div className="admin-muted" style={{ marginTop: 4 }}>
                  Folder: <b>{editingFolder.name}</b>
                </div>
              </div>
              <button className="btn btn-secondary" type="button" onClick={closePermModal}>
                ✕
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <div className="admin-muted" style={{ marginBottom: 8 }}>
                Select groups that can access this folder.
              </div>

              {selectableGroupNames.length === 0 ? (
                <div className="admin-muted">No groups available.</div>
              ) : (
                <div className="admin-chip-grid">
                  {selectableGroupNames.map((name) => (
                    <label key={name} className="admin-chip">
                      <input
                        type="checkbox"
                        checked={editAllowedGroups.includes(name)}
                        onChange={() => toggleEditGroup(name)}
                        disabled={savingPerms}
                      />
                      <span>{name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="form-actions" style={{ marginTop: 14 }}>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={closePermModal}
                disabled={savingPerms}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={savePermissions}
                disabled={savingPerms}
              >
                {savingPerms ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
function getFoldersForUser(userData) {
    const role = userData?.role || "user";
    const userGroups = userData?.groups || [];

    // Admin sees everything
    if (role === "admin") {
      return getDocs(collection(db, "folders"));
    }
    
    // Uploader (or normal user in this panel): only folders that match their groups
    if (!userGroups.length) {
      // no groups => no folders visible
      return Promise.resolve({ docs: [] });
    }

    // Firestore limit: array-contains-any max 10 values
    const q = query(
      collection(db, "folders"),
      where("allowedGroups", "array-contains-any", userGroups.slice(0, 10))
    );

    return getDocs(q);
}

